import { Fragment, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Tile from "components/Tile";
import TileBody from "components/TileBody";
import RadioGroupPack from "components/RadioGroupPack";
import * as utils from "common/utils";
import * as domain from "common/domain";
import Alert from "components/Alert";
import numeral from "numeral";
import * as animalUtils from "../_utils";
import Watermark from "components/Watermark";
import { enterpriseViewModel } from "common/viewModels";
import ActionLink from "components/ActionLink";

export default function PastureAndDiet({ farm, budget, collapsed }) {
    const refData = useSelector((state) => state.app.refData);
    const showQuickTips = useSelector((state) => state.app.showQuickTips);
    const [enterpriseId, setEnterpriseId] = useState((budget.enterprises || []).length > 0 ? budget.enterprises[0].id : undefined);
    const [options, setOptions] = useState([
        { value: "mj", text: "MJ ME/Month", checked: true },
        { value: "rsu", text: "RSU", checked: false },
        { value: "percent", text: "% distribution from available sources", checked: false },
    ]);
    const [sourceType, setSourceType] = useState("mj");
    const [expandedSections, setExpandedSections] = useState([]);

    useEffect(() => setSourceType(options.find((opt) => opt.checked).value), [options]);

    const sourceTypeChanged = (e) => setOptions((prevState) => prevState.map((opt) => ({ ...opt, checked: e.target.id === `sourceType_${opt.value}` })));

    const expandSection = (section) => () =>
        setExpandedSections((prevState) => {
            return prevState.some((s) => s === section) ? prevState.filter((s) => s !== section) : [...prevState, section];
        });

    const { enterprises = [] } = budget;
    const { currentResults = {} } = budget;
    const { animalResults = {} } = currentResults;
    const { enterpriseTypes = [] } = refData;
    const { sources = {} } = animalResults[enterpriseId] || {};
    const enterprise = enterprises.find((e) => e.id === enterpriseId) || {};
    const { mobs = [] } = enterprise;

    const enterpriseVm = enterpriseViewModel(farm, budget, refData);
    const enterpriseSummary = enterpriseVm.find((e) => e.id === enterpriseId);

    // for graph
    let total = 0;
    let totalValues = {};
    const showStockCounts = expandedSections.includes("StockCount");

    const blocksWithPasture = budget.blocks.filter((b) => b.currentResults && b.currentResults.pastureResults && b.currentResults.pastureResults.pastureRsu && b.currentResults.pastureResults.pastureRsu[enterpriseId] && b.currentResults.pastureResults.pastureRsu[enterpriseId].length > 0);

    const mj = options[0];
    const rsu = options[1];
    const percent = options[2];
    const showPercentageOfMERequirements = sourceType === mj.value;

    const totalMonths = [];

    sources &&
        Object.keys(sources).forEach((srcKey) => {
            domain.farmYear.forEach((farmMonth) => {
                const stMonth = sources[srcKey].produced.find((ms) => ms.month === farmMonth);
                if (stMonth && stMonth.value > 0 && !totalMonths.includes(farmMonth)) {
                    totalMonths.push(farmMonth);
                }
            });
        });

    const monthsUsedCount = totalMonths.length > 0 ? totalMonths.length : 12;

    return (
        <Tile title="Block pasture &amp; diet (ME source)" secondary collapsible open={!collapsed} className="u-page-break">
            <Alert type="info" text="Shows the feed requirements and source for each enterprise. The first table shows the RSU due to pasture intake by block and month. The second table shows the proportion of the diet for each source by month. Select the enterprise name to switch between each enterprise. You can expand each source to view where the feed was distributed." />
            {showQuickTips && (
                <Alert
                    type="help"
                    text={
                        <div>
                            The diet (ME source) table shows the proportion of the diet for each month (ME from a given source for that month divided by the ME supplied by all sources for that month). The sources are:
                            <ul>
                                <li>
                                    <b>Pasture: </b>From fresh pasture
                                </li>
                                <li>
                                    <b>Farm supplements: </b>From farm grown and stored supplements (hay, silage and baleage).
                                </li>
                                <li>
                                    <b>Fodder supplements: </b>From imported fodder supplements (hay, silage, straws and maize).
                                </li>
                                <li>
                                    <b>Other supplements: </b>From other imported and left over supplements.
                                </li>
                                <li>
                                    <b>Crops: </b>From farm grown crops.
                                </li>
                                <li>
                                    <b>Animal ME requirements: </b>The ratio of ME intake from all sources divided by animal ME requirements if % is selected, otherwise it shows animal ME requirements per RSU or ME intake. There is a buffer of about 20% on feed requirements, that is, the model will provide reports when the feed supply is within 20% of animal requirement. Hence ME from feed may not equal animal ME requirements.
                                </li>
                            </ul>
                        </div>
                    }
                />
            )}
            <TileBody>
                {(!sources || Object.keys(sources).length === 0) && <Alert type="info" text="There are currently no animal results for this analysis. Please correct any errors to run the report." />}
                {sources && Object.keys(sources).length > 0 && (
                    <div>
                        <ul className="SubMenu">
                            {enterprises.map((e, i) => (
                                <li key={i} className={`SubMenu-item ${e.id === enterpriseId ? "is-active" : ""}`}>
                                    <ActionLink onClick={() => setEnterpriseId(e.id)}>
                                        <span>{utils.valueToText(enterpriseTypes, e.type)}</span>
                                    </ActionLink>
                                </li>
                            ))}
                        </ul>
                        <Watermark />
                        {enterpriseSummary && (
                            <div className="Table u-mt-md">
                                <table>
                                    <thead>
                                        <tr>
                                            <th data-width="20" className="td--alignTop" colSpan={enterpriseSummary.slots.length > 0 ? 1 : 2}>
                                                Stock numbers
                                            </th>
                                            {enterpriseSummary.type === "Standard" && enterpriseSummary.slots.length > 0 && (
                                                <Fragment>
                                                    {enterpriseSummary.slots.map((m) => (
                                                        <th key={m.month} className="u-textCenter">
                                                            {m.month.substring(0, 3)}
                                                        </th>
                                                    ))}
                                                    <th data-width="7.5" className="u-textCenter"></th>
                                                </Fragment>
                                            )}
                                            {enterpriseSummary.type !== "Standard" && enterpriseSummary.slots.length > 0 && <th></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td data-width="25">
                                                {mobs.length > 0 ? (
                                                    <ActionLink className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween" onClick={expandSection("StockCount")}>
                                                        {enterpriseSummary.enterpriseType}
                                                        <i className={`icon icon-arrow-${showStockCounts ? "up" : "down"}`}></i>
                                                    </ActionLink>
                                                ) : (
                                                    <span>{enterpriseSummary.enterpriseType}</span>
                                                )}
                                            </td>
                                            {enterpriseSummary.type === "Standard" && enterpriseSummary.slots.length > 0 && (
                                                <Fragment>
                                                    {enterpriseSummary.slots.map((m) => (
                                                        <td key={m.month} className="u-textCenter">
                                                            {m.text}
                                                        </td>
                                                    ))}
                                                    <td data-width="7.5" className="u-textCenter"></td>
                                                </Fragment>
                                            )}
                                            {enterpriseSummary.type === "Rsu" && (
                                                <td>
                                                    <ul className="DataWidthTable FarmTable">
                                                        <li data-width="md-50" style={{ padding: "5px 20px" }}>
                                                            <span className="inline-key">Total RSU: </span>
                                                            <span className="inline-value">{enterpriseSummary.totalRsu}</span>
                                                        </li>
                                                    </ul>
                                                </td>
                                            )}
                                            {enterpriseSummary.type === domain.EnterpriseTypes.OutdoorPigs && (
                                                <td>
                                                    <ul className="DataWidthTable FarmTable">
                                                        <li data-width="md-50" style={{ padding: "5px 20px" }}>
                                                            <span className="inline-key">{enterpriseSummary.growOutUnitOnly ? "Growers brought in per week: " : "Number of sows and mated gilts: "}</span>
                                                            <span className="inline-value">{enterpriseSummary.growOutUnitOnly ? enterpriseSummary.numbers.growersFinishers.broughtIn : enterpriseSummary.numbers.sows.numberSowsMatedGilts}</span>
                                                        </li>
                                                    </ul>
                                                </td>
                                            )}
                                            {enterpriseSummary.type === "DairyPeak" && (
                                                <td>
                                                    <ul className="DataWidthTable FarmTable">
                                                        <li data-width="md-50" style={{ padding: "5px 20px" }}>
                                                            <span className="inline-key">Peak number of cows milked: </span>
                                                            <span className="inline-value">{enterpriseSummary.peakCowsMilked}</span>
                                                        </li>
                                                        <li data-width="md-50" style={{ padding: "5px 20px" }}>
                                                            <span className="inline-key">AVERAGE MOB WEIGHT: </span>
                                                            <span className="inline-value"> {enterpriseSummary.averageMobWeight ? `${enterpriseSummary.averageMobWeight}kg` : "Use default"}</span>
                                                        </li>
                                                    </ul>
                                                </td>
                                            )}
                                        </tr>
                                        {showStockCounts &&
                                            mobs.map((livestock, index) => {
                                                livestock.stockCounts = animalUtils.getStockCountsForMob(livestock);
                                                const livestockDetails = animalUtils.getLivestockDetails(livestock, refData);
                                                const startWeight = animalUtils.getStartWeight(livestock.events);
                                                const start = livestock.events.find((e) => e.eventType === "Starting" || e.month === "July");
                                                const age = start && start.ageInMonths;
                                                const { stockCounts = [] } = livestock;
                                                return (
                                                    <tr key={index} className="tr-nested">
                                                        <td data-width="25">
                                                            {livestock.name}
                                                            <br />
                                                            <span className="Table-subtitle">Class: {livestockDetails.class}</span>
                                                            <span className="Table-subtitle"> | Breed: {livestockDetails.breed}</span>
                                                            {age ? <span className="Table-subtitle"> | Age: {age} months</span> : null}
                                                            {startWeight ? <span className="Table-subtitle"> | Start Weight: {startWeight}kg</span> : null}
                                                        </td>
                                                        {stockCounts.map((stockCount) => (
                                                            <td key={stockCount.month} className={`u-textCenter ${stockCount.stockCount < 0 ? "u-textError" : ""}`}>
                                                                {stockCount.stockCount === 0 ? "-" : stockCount.stockCount}
                                                            </td>
                                                        ))}
                                                        <td data-width="7.5" className="u-textCenter"></td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                    {enterpriseSummary.slots.length === 0 && (
                                        <tbody>
                                            <tr>
                                                <td data-width="25">{enterpriseSummary.title}</td>
                                                <td>{enterpriseSummary.noMonthsMessage}</td>
                                            </tr>
                                        </tbody>
                                    )}
                                </table>
                            </div>
                        )}

                        {blocksWithPasture.length > 0 && (
                            <div className="Table u-mt-md">
                                <table>
                                    <thead>
                                        <tr>
                                            <th rowSpan="2" data-width="25" className="td--alignTop">
                                                Pasture (RSU/ha/month)
                                            </th>
                                            <th colSpan="13">{"Average annual pastoral RSU per hectare: " + animalResults[enterpriseId].averagePastoralRsu}</th>
                                        </tr>
                                        <tr>
                                            {domain.farmYear.map((m) => (
                                                <th key={m} className="u-textCenter">
                                                    {m.substring(0, 3)}
                                                </th>
                                            ))}
                                            <th data-width="7.5" className="u-textCenter">
                                                Total
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {blocksWithPasture.map((b, i) => {
                                            const rsu = b.currentResults.pastureResults.pastureRsu[enterpriseId];
                                            let total = 0;
                                            return (
                                                <tr key={i} id={utils.strToId(b.name)}>
                                                    <td data-width="25" id={utils.strToId(b.name)}>
                                                        {b.name}
                                                    </td>
                                                    {domain.farmYear.map((m, j) => {
                                                        const rsuForMonth = rsu ? rsu.find((r) => r.month === m) : undefined;
                                                        total += rsuForMonth ? rsuForMonth.value : 0;
                                                        return (
                                                            <td key={m + i} className="u-textCenter" id={utils.strToId(`${b.name}_${m}`)}>
                                                                {rsuForMonth === undefined ? "-" : rsuForMonth.value === 0 ? "-" : numeral(rsuForMonth.value).format("0.00")}
                                                            </td>
                                                        );
                                                    })}
                                                    <td data-width="7.5" className="u-textCenter" id={utils.strToId(`${b.name}_total`)}>
                                                        {numeral(total).format("0.00")}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="Table u-mt-md">
                            <table>
                                <thead>
                                    <tr>
                                        <th rowSpan="2" data-width="25" className="td--alignTop">
                                            Diet
                                        </th>
                                        <th colSpan="13">
                                            <RadioGroupPack id="sourceType" name="sourceType" meta={{ nrf: true }} options={options} inline={true} onChange={sourceTypeChanged} />
                                        </th>
                                    </tr>
                                    <tr>
                                        {domain.farmYear.map((m) => (
                                            <th key={m} className="u-textCenter">
                                                {m.substring(0, 3)}
                                            </th>
                                        ))}
                                        <th data-width="7.5" className="u-textCenter">
                                            {sourceType === percent.value ? "% diet" : "Total"}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(sources).map((srcKey) => {
                                        const sourceName = utils.valueToText(refData.animalFeedSources, srcKey) || srcKey;
                                        let lineTotal = 0;
                                        let monthlyValues = {};
                                        const hasChildren = sourceType !== rsu.value && sources[srcKey].locations.length > 0;
                                        const isSectionExpanded = expandedSections.includes(srcKey);
                                        return (
                                            <Fragment key={srcKey}>
                                                <tr id={utils.strToId(sourceName)}>
                                                    <td data-width="25">
                                                        {hasChildren && (
                                                            <ActionLink className="u-flex u-flexAlignItemsCenter u-flexJustifyBetween" onClick={expandSection(srcKey)}>
                                                                {sourceName}
                                                                <i className={`icon icon-arrow-${isSectionExpanded ? "up" : "down"}`}></i>
                                                            </ActionLink>
                                                        )}
                                                        {!hasChildren && sourceName}
                                                    </td>
                                                    {domain.farmYear.map((farmMonth, ci) => {
                                                        const stMonth = sources[srcKey].produced.find((ms) => ms.month === farmMonth) || { proportion: 0, perAnimal: 0, value: 0 };

                                                        if (!monthlyValues[farmMonth]) monthlyValues[farmMonth] = {};
                                                        if (!totalValues[farmMonth]) totalValues[farmMonth] = 0;

                                                        let value = 0;
                                                        switch (sourceType) {
                                                            case rsu.value:
                                                                value = stMonth.perAnimal;
                                                                break;
                                                            case mj.value:
                                                                value = stMonth.value;
                                                                break;
                                                            default:
                                                                value = utils.round(stMonth.proportion * 100, 2);
                                                                break;
                                                        }
                                                        monthlyValues[farmMonth].proportion = stMonth.proportion;
                                                        monthlyValues[farmMonth].mj = stMonth.value;
                                                        totalValues[farmMonth] += value;
                                                        lineTotal += value;

                                                        const monthlyMetabolicRequirement = animalResults[enterpriseId].monthlyResults.MetabolicRequirements.results[farmMonth];
                                                        const sourceAsPercentageOfRequirement = showPercentageOfMERequirements && monthlyMetabolicRequirement > 0 ? utils.round((value / monthlyMetabolicRequirement) * 100, 1) : undefined;

                                                        return (
                                                            <td key={utils.strToId(sourceName) + "-" + ci} className={`u-textCenter ${sourceAsPercentageOfRequirement > 120 ? "Alert--error" : ""}`} id={utils.strToId(`${sourceName}_${farmMonth}`)}>
                                                                {value === 0 && <div>-</div>}
                                                                {value > 0 && sourceType === mj.value && <div>{numeral(value).format("0,0")}</div>}
                                                                {value > 0 && sourceType === rsu.value && <div>{numeral(value).format("0.0")}</div>}
                                                                {value > 0 && sourceType === percent.value && <div>{numeral(value).format("0.0")}%</div>}
                                                                {sourceAsPercentageOfRequirement > 0 && (
                                                                    <div className={`small ${sourceAsPercentageOfRequirement > 120 ? "u-textError" : ""}`}>
                                                                        {sourceAsPercentageOfRequirement}%<sup>**</sup>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                    <td data-width="7.5" className="u-textRight" id={utils.strToId(`${sourceName}_total`)}>
                                                        {sourceType === percent.value ? numeral(lineTotal / monthsUsedCount).format("0.0") : numeral(lineTotal).format("0,0")}
                                                    </td>
                                                </tr>
                                                {sourceType !== rsu.value &&
                                                    isSectionExpanded &&
                                                    sources[srcKey].locations.map((l, li) => {
                                                        let subLineTotal = 0;
                                                        return (
                                                            <tr key={utils.strToId(l.name)} className={`tr-nested`}>
                                                                <td>{l.name}</td>
                                                                {domain.farmYear.map((m, si) => {
                                                                    let locMonth = l.months.find((ms) => ms.month === m) || { value: 0 };
                                                                    const monthResult = sourceType === percent.value ? (100 * monthlyValues[m].proportion * locMonth.value) / monthlyValues[m].mj : locMonth.value;
                                                                    subLineTotal += isNaN(monthResult) ? 0 : monthResult;
                                                                    return (
                                                                        <td key={utils.strToId(l.name) + "-" + si} className="u-textRight">
                                                                            {isNaN(monthResult) || monthResult === 0 ? "-" : sourceType === percent.value ? numeral(monthResult).format("0.0") : numeral(monthResult).format("0,0")}
                                                                        </td>
                                                                    );
                                                                })}
                                                                <td className="u-textRight">{sourceType === percent.value ? numeral(subLineTotal / monthsUsedCount).format("0.0") : numeral(subLineTotal).format("0,0")}</td>
                                                            </tr>
                                                        );
                                                    })}
                                            </Fragment>
                                        );
                                    })}

                                    <tr key="Total" id="Total">
                                        <th data-width="25">Total</th>
                                        {domain.farmYear.map((m, ci) => {
                                            total += totalValues[m];
                                            return (
                                                <th key={ci} className="u-textRight" id={`${m}_total`}>
                                                    <h4 className="u-mt-0">{totalValues[m] ? numeral(totalValues[m]).format("0,0") : "-"}</h4>
                                                </th>
                                            );
                                        })}
                                        <th data-width="7.5" className="u-textRight" id="overall_total">
                                            <h4 className="u-mt-0">{sourceType === percent.value ? 100 : numeral(total).format("0,0")}</h4>
                                        </th>
                                    </tr>
                                </tbody>
                                {sourceType === percent.value && (
                                    <tfoot>
                                        <tr className="u-border-b u-border-e">
                                            <td></td>
                                            <td colSpan="13" className="u-textCenter u-textGrey">
                                                <i>If percentages do not fully add up to 100% it is to do with the summing of rounded values.</i>
                                            </td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                        {showPercentageOfMERequirements && (
                            <div>
                                <sub>** Percentage of ME requirements from this source. Anything over 120% of ME requirements is considered an overfeed error.</sub>
                            </div>
                        )}
                    </div>
                )}
            </TileBody>
        </Tile>
    );
}
